import React, { useState, useEffect, useRef } from 'react';
import axios from '../../../../config/axios';
import {
  InputDiv,
  Label,
  Input,
  SearchOptions,
  Option,
  VectorIcon,
} from '../Select/select-style';
import vector from '../../../../assets/equipment/vector.svg';
import ReactDOM from 'react-dom';

interface ItemType {
  id: number;
  [key: string]: any;
}

interface CompanySelectProps<T extends ItemType | null> {
  label: string;
  apiUrl: string;
  displayField: string;
  queryParams?: Record<string, string | number>;
  value: T;
  onChange: (item: T) => void;
  isClearable?: boolean;
}

const CompanySelect = <T extends ItemType | null>({
  label,
  apiUrl,
  displayField,
  queryParams = {},
  value,
  onChange,
}: CompanySelectProps<T>) => {
  const [items, setItems] = useState<ItemType[]>([]);
  const [allCompanies, setAllCompanies] = useState<ItemType[]>([]);
  const [inputItem, setInputItem] = useState<string>(
    (value?.[displayField] as string) || '',
  );
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  useEffect(() => {
    setInputItem((value?.[displayField] as string) || '');
  }, [value, displayField]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get<ItemType[]>(apiUrl, {
          params: { ...queryParams, loadQuantity: 1000 },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (Array.isArray(response.data)) {
          const companies = response.data;
          setAllCompanies(companies);
          setItems(companies);
        } else {
          console.error('Invalid API response, expected an array.');
          setAllCompanies([]);
          setItems([]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setAllCompanies([]);
        setItems([]);
      }
    };
    fetchCompanies();
  }, [apiUrl, JSON.stringify(queryParams)]);

  useEffect(() => {
    if (showOptions && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [showOptions, inputRef]);

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputItem(newValue);
    setSelectedIndex(-1);

    const filterCriteria = newValue.toLowerCase();
    const filteredItems = allCompanies.filter((item) =>
      (item[displayField] as string)?.toLowerCase()?.includes(filterCriteria),
    );
    setItems(filteredItems);

    setShowOptions(true);
    if (newValue === '') {
      onChange(null as T);
    }
  };

  const handleSelectItem = (item: ItemType) => {
    setInputItem(item[displayField] as string);
    setShowOptions(false);
    onChange(item as T);
  };

  const handleSetOptionByKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < items.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex !== -1 && items[selectedIndex]) {
        handleSelectItem(items[selectedIndex]);
      } else {
        setShowOptions(false);
        onChange(null as T);
      }
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      const selectedItem = items.find(
        (item) =>
          (item[displayField] as string)?.toLowerCase() ===
          inputItem.toLowerCase(),
      );

      if (!selectedItem) {
        setInputItem('');
        onChange(null as T);
      }
      setShowOptions(false);
    }, 150);
  };

  return (
    <InputDiv>
      <Label>{label}</Label>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <Input
          ref={inputRef}
          type='text'
          placeholder='Selecione'
          value={inputItem}
          onFocus={() => setShowOptions(true)}
          onChange={handleChangeInput}
          onKeyDown={handleSetOptionByKey}
          onBlur={handleInputBlur}
        />
        <VectorIcon src={vector} alt="dropdown arrow" />
      </div>
      {showOptions &&
        items.length > 0 &&
        ReactDOM.createPortal(
          <SearchOptions
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
            }}
          >
            {items.map((s, index) => (
              <Option
                key={s.id}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelectItem(s)}
                isSelected={index === selectedIndex}
              >
                {s[displayField] as React.ReactNode}
              </Option>
            ))}
          </SearchOptions>,
          document.body,
        )}
    </InputDiv>
  );
};

export default CompanySelect;
